// src/store/AdminStore.js
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useFetch } from '@/plugins/fetch';

export const useAdminStore = defineStore('admin', () => {
  const fetch = useFetch();

  // State
  const sensorTypes = ref([]);
  const users = ref([]);
  const systemStats = ref({});
  const isLoading = ref(false);

  // Error states - per-resource for better UX
  const errors = ref({
    sensorTypes: null,
    users: null,
    stats: null,
  });

  // Clear errors
  function clearErrors() {
    errors.value = { sensorTypes: null, users: null, stats: null };
  }

  function clearError(key) {
    errors.value[key] = null;
  }

  // --- Sensor Types ---
  async function fetchSensorTypes(force = false) {
    if (sensorTypes.value.length > 0 && !force) {
      return;
    }
    isLoading.value = true;
    errors.value.sensorTypes = null;
    try {
      const response = await fetch('admin/sensor-types/');
      sensorTypes.value = Array.isArray(response) ? response : Object.values(response);
    } catch (err) {
      errors.value.sensorTypes = err?.message || 'Failed to fetch sensor types';
    } finally {
      isLoading.value = false;
    }
  }

  async function createSensorType(sensorTypeData) {
    isLoading.value = true;
    errors.value.sensorTypes = null;
    try {
      const response = await fetch('admin/sensor-types/', {
        method: 'POST',
        body: sensorTypeData,
      });
      sensorTypes.value.push(response);
      return response;
    } catch (err) {
      errors.value.sensorTypes = err?.message || 'Failed to create sensor type';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateSensorType(id, sensorTypeData) {
    isLoading.value = true;
    errors.value.sensorTypes = null;
    try {
      const response = await fetch(`admin/sensor-types/${id}/`, {
        method: 'PUT',
        body: sensorTypeData,
      });
      const index = sensorTypes.value.findIndex((st) => st.id === id);
      if (index !== -1) {
        sensorTypes.value[index] = response;
      }
      return response;
    } catch (err) {
      errors.value.sensorTypes = err?.message || 'Failed to update sensor type';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function deleteSensorType(id) {
    isLoading.value = true;
    errors.value.sensorTypes = null;
    try {
      await fetch(`admin/sensor-types/${id}/`, {
        method: 'DELETE',
      });
      sensorTypes.value = sensorTypes.value.filter((st) => st.id !== id);
    } catch (err) {
      errors.value.sensorTypes = err?.message || 'Failed to delete sensor type';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // --- Users ---
  async function fetchUsers(force = false) {
    if (users.value.length > 0 && !force) {
      return;
    }
    isLoading.value = true;
    errors.value.users = null;
    try {
      const response = await fetch('admin/users/');
      users.value = Array.isArray(response) ? response : Object.values(response);
    } catch (err) {
      errors.value.users = err?.message || 'Failed to fetch users';
    } finally {
      isLoading.value = false;
    }
  }

  async function createUser(userData) {
    isLoading.value = true;
    errors.value.users = null;
    try {
      const response = await fetch('admin/users/', {
        method: 'POST',
        body: userData,
      });
      users.value.push(response);
      return response;
    } catch (err) {
      errors.value.users = err?.message || 'Failed to create user';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function updateUser(id, userData) {
    isLoading.value = true;
    errors.value.users = null;
    try {
      const response = await fetch(`admin/users/${id}/`, {
        method: 'PUT',
        body: userData,
      });
      const index = users.value.findIndex((u) => u.id === id);
      if (index !== -1) {
        users.value[index] = response;
      }
      return response;
    } catch (err) {
      errors.value.users = err?.message || 'Failed to update user';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function deleteUser(id) {
    isLoading.value = true;
    errors.value.users = null;
    try {
      await fetch(`admin/users/${id}/`, {
        method: 'DELETE',
      });
      users.value = users.value.filter((u) => u.id !== id);
    } catch (err) {
      errors.value.users = err?.message || 'Failed to delete user';
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  // --- System Stats ---
  async function fetchSystemStats() {
    isLoading.value = true;
    errors.value.stats = null;
    try {
      const response = await fetch('admin/stats/');
      systemStats.value = response;
    } catch (err) {
      errors.value.stats = err?.message || 'Failed to fetch system stats';
    } finally {
      isLoading.value = false;
    }
  }

  // --- Getters ---
  const getSensorTypeById = (id) => {
    return sensorTypes.value.find((st) => st.id === id);
  };

  const getUserById = (id) => {
    return users.value.find((u) => u.id === id);
  };

  const enabledSensorTypes = () => {
    return sensorTypes.value.filter((st) => st.enabled);
  };

  return {
    // State
    sensorTypes,
    users,
    systemStats,
    isLoading,
    errors,
    // Error handling
    clearErrors,
    clearError,
    // Sensor Types Actions
    fetchSensorTypes,
    createSensorType,
    updateSensorType,
    deleteSensorType,
    // Users Actions
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    // Stats
    fetchSystemStats,
    // Getters
    getSensorTypeById,
    getUserById,
    enabledSensorTypes,
  };
});
